import { useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import type { CustomerRevenue, CustomerSearch, CustomerSearchRow } from '@metron/shared';
import {
  parseCustomerSearchParams,
  Permission,
  SEARCH_DEFAULT_PAGE_SIZE,
  toCustomerSearchParams,
} from '@metron/shared';
import { DataTable } from '../components/data-table/data-table.tsx';
import { DataTableColumnHeader } from '../components/data-table/data-table-column-header.tsx';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.tsx';
import { CustomerSearchFilters } from '../components/customer-search/CustomerSearchFilters.tsx';
import { useTableUrlState } from '../hooks/useTableUrlState.ts';
import { useCustomerFilter } from '../hooks/useCustomerFilter.ts';
import { useCustomerRevenue } from '../hooks/useCustomerRevenue.ts';
import { usePermissions } from '../hooks/usePermissions.ts';
import { REVENUE_CAPTION, RevenueAmount } from '../components/CustomerRevenue.tsx';

// Column ids match the spec's sortBy values, so useTableUrlState's `sortBy`
// param is directly the spec's. Status isn't a server sort key.
const baseColumns: ColumnDef<CustomerSearchRow>[] = [
  {
    accessorKey: 'waterscopeAccountName',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Customer' />,
    cell: ({ row }) => <div className='font-medium'>{row.original.waterscopeAccountName}</div>,
  },
  {
    accessorKey: 'accountType',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Account Type' />,
    cell: ({ row }) =>
      row.original.realEstateAccountType
        ? `${row.original.accountType} (${row.original.realEstateAccountType})`
        : row.original.accountType,
  },
  {
    accessorKey: 'sensorCount',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Assigned' />,
    cell: ({ row }) => <div className='text-right'>{row.original.sensorCount.toLocaleString()}
    </div>,
  },
  {
    accessorKey: 'installedSensorCount',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Installed' />,
    cell: ({ row }) => (
      <div className='text-right'>{row.original.installedSensorCount.toLocaleString()}</div>
    ),
  },
  // Revenue (Admin only) is spliced in here; see `revenueColumn`.
  {
    accessorKey: 'active',
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title='Status' />,
    cell: ({ row }) => (row.original.active ? 'Active' : 'Inactive'),
  },
  {
    accessorKey: 'createdAt',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Created' />,
    cell: ({ row }) => new Date(row.original.createdAt).toLocaleDateString(),
  },
];

const REVENUE_COLUMN_INDEX = 4;

/**
 * Revenue (T12M), VIEW_REVENUE only (MET-3629). The server sorts and filters by
 * the row's `revenueT12m`; the cell renders from GET /customers/revenue like
 * the customers list, so it shows the same dash reasons and heuristic marker.
 */
function revenueColumn(
  revenueById: Map<number, CustomerRevenue> | null,
): ColumnDef<CustomerSearchRow> {
  return {
    id: 'revenueT12m',
    accessorFn: (row) => row.revenueT12m,
    header: ({ column }) => <DataTableColumnHeader column={column} title='Revenue (T12M)' />,
    cell: ({ row }) =>
      revenueById
        ? (
          <div className='text-right'>
            <RevenueAmount
              revenue={revenueById.get(row.original.id)}
              pick={(r) => r.trailing12m}
              compact
            />
          </div>
        )
        : null,
  };
}

/**
 * Customer Search (Smart Search results, MET-3545)
 *
 * The customer counterpart of Meter Search: the whole search lives in the URL
 * (the MET-3525 spec), filters render as editable chips, and paging/sorting
 * use the shared URL-backed table state under the spec's own keys.
 */
export function CustomerSearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { pagination, sorting, onPaginationChange, onSortingChange } = useTableUrlState({
    defaultPageSize: SEARCH_DEFAULT_PAGE_SIZE,
  });

  const parsed = useMemo(() => parseCustomerSearchParams(searchParams), [searchParams]);
  const spec = parsed.success ? parsed.data : null;
  const { data, isLoading, isFetching, error } = useCustomerFilter(spec);

  const { hasPermission } = usePermissions();
  const canViewRevenue = hasPermission(Permission.VIEW_REVENUE);
  const { data: revenueData } = useCustomerRevenue(canViewRevenue);
  const columns = useMemo(() => {
    if (!canViewRevenue) return baseColumns;
    const revenueById = revenueData
      ? new Map(revenueData.customers.map((r) => [r.customerId, r]))
      : null;
    return [
      ...baseColumns.slice(0, REVENUE_COLUMN_INDEX),
      revenueColumn(revenueById),
      ...baseColumns.slice(REVENUE_COLUMN_INDEX),
    ];
  }, [canViewRevenue, revenueData]);

  const setSpec = (next: CustomerSearch) => setSearchParams(toCustomerSearchParams(next));

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-2xl font-bold tracking-tight'>Customer Search</h1>
        <p className='text-muted-foreground'>
          Filter customers by type, status, and meter counts. Share the URL to share the search.
          {canViewRevenue && ` Revenue: ${REVENUE_CAPTION}`}
        </p>
      </div>

      {!parsed.success
        ? (
          <Card>
            <CardHeader>
              <CardTitle>This search link isn't valid</CardTitle>
              <CardDescription>
                {parsed.error.issues.map((i) => `${i.path.join('.') || 'search'}: ${i.message}`)
                  .join('; ')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link className='underline' to='/search/customers'>Start a new search</Link>
            </CardContent>
          </Card>
        )
        : (
          <Card>
            <CardHeader className='space-y-4'>
              <CustomerSearchFilters spec={parsed.data} onChange={setSpec} />
              <CardDescription aria-live='polite'>
                {isLoading
                  ? 'Searching…'
                  : error
                  ? null
                  : `${(data?.total ?? 0).toLocaleString()} customer${
                    data?.total === 1 ? '' : 's'
                  }${isFetching ? ' · updating…' : ''}`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {error
                ? (
                  <p role='alert' className='text-sm text-destructive py-4 text-center'>
                    Search failed: {error instanceof Error ? error.message : 'unknown error'}
                  </p>
                )
                : (
                  <DataTable
                    columns={columns}
                    data={data?.customers ?? []}
                    onRowClick={(customer) => navigate(`/customers/${customer.id}`)}
                    manualPagination
                    rowCount={data?.total ?? 0}
                    pagination={pagination}
                    onPaginationChange={onPaginationChange}
                    manualSorting
                    sorting={sorting}
                    onSortingChange={onSortingChange}
                    emptyMessage={isLoading ? 'Searching…' : 'No customers match these filters.'}
                  />
                )}
            </CardContent>
          </Card>
        )}
    </div>
  );
}
